"use client";
import { Box, Card, Stack, Typography } from "@mui/material";
import CarouselHeader from "@/app/(main)/_components/MainPage/CarouselHeader";
import { PropsWithChildren, ReactNode } from "react";
import useEmblaCarousel from "embla-carousel-react";
import CarouselFooter from "./CarouselFooter";
import { SProduct } from "@/__generated__/sdk";
import Image from "next/image";
import ProductCard from "@/app/(main)/_components/SITESEARCH/ProductCard";
import ProductCardSkeleton from "../SITESEARCH/ProductCardSkeleton";
import CarouselShowMoreCard from "../CarouselShowMoreCard";
import CarouselFadeSides from "./CarouselFadeSides";
import { DiscoverySurface } from "@/lib/productDiscovery/trackProductDiscovery";

type Props = {
  title: string;
  icon?: ReactNode;
  href?: string;
  items?: SProduct[];
  xlCount?: number;
  lgCount?: number;
  mdCount?: number;
  loading?: boolean;
  color?: string | null;
  iconImage?: string | null;
  showMore?: boolean;
  /** Override default top margin (CardCarousel defaults to 5) */
  mt?: number | Record<string, number>;
  surface?: DiscoverySurface;
};

function NotFoundCard() {
  return (
    <Card
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 2,
        gap: 1,
        minWidth: 0,
        width: "100%",
        userSelect: "none",
        background: "transparent",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: { xs: 250, md: 320 },
          aspectRatio: 1,
        }}
      >
        <Image unoptimized src={"/errors/sadBasket.svg"} fill alt={"sadBasket"} style={{ objectFit: "contain" }} />
      </Box>
      <Typography variant="body2">محصولی پیدا نشد</Typography>
    </Card>
  );
}

function CardBox({
  children,
  xlNum,
  lgNum,
  mdNum,
}: PropsWithChildren<{ xlNum: number; lgNum: number; mdNum: number }>) {
  const slideSpacingXs = "0.5rem";
  const slideSizeXs = "calc(92% / 2)";
  const slideSpacingSm = "0.7rem";
  const slideSizeSm = "calc(90% / 3)";
  const slideSpacingMd = "0.8rem";
  const slideSizeMd = `calc(90% / ${mdNum})`;
  const slideSpacingLg = "0.9rem";
  const slideSizeLg = `calc(95% / ${lgNum})`;
  const slideSpacingXl = "1rem";
  const slideSizeXl = `calc(95% / ${xlNum})`;

  return (
    <Box
      sx={{
        minWidth: 0,
        flex: {
          xs: `0 0 ${slideSizeXs}`,
          sm: `0 0 ${slideSizeSm}`,
          md: `0 0 ${slideSizeMd}`,
          lg: `0 0 ${slideSizeLg}`,
          xl: `0 0 ${slideSizeXl}`,
        },
        pr: {
          xs: `${slideSpacingXs}`,
          sm: `${slideSpacingSm}`,
          md: `${slideSpacingMd}`,
          lg: `${slideSpacingLg}`,
          xl: `${slideSpacingXl}`,
        },
      }}
    >
      {children}
    </Box>
  );
}

export default function CardCarousel({ title, color, icon, href, items, xlCount, lgCount, mdCount, loading, showMore, iconImage, mt = 5, surface }: Props) {
  const xlNum = xlCount || 5;
  const lgNum = lgCount || 5;
  const mdNum = mdCount || 3;

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, direction: "rtl", dragFree: true, align: "start" });

  const slideSpacingXs = "0.5rem";
  const slideSpacingSm = "0.7rem";
  const slideSpacingMd = "0.8rem";
  const slideSpacingLg = "0.9rem";
  const slideSpacingXl = "1rem";

  return (
    <>
      <Stack mt={mt} spacing={2}>
        <CarouselHeader title={title} icon={icon} href={href} emblaApi={emblaApi as any} />
        {/* embla */}
        <Box width={"100%"} sx={{ position: "relative" }}>
          {/*  embla__viewport */}
          <Box sx={{ overflow: "hidden", position: "relative" }} ref={emblaRef}>
            {/* embla__container */}
            <Box
              sx={{
                backfaceVisibility: "hidden",
                display: "flex",
                touchAction: "pan-y pinch-zoom",
                py: 0.2,
                mr: {
                  xs: `calc(${slideSpacingXs} * -1)`,
                  sm: `calc(${slideSpacingSm} * -1)`,
                  md: `calc(${slideSpacingMd} * -1)`,
                  lg: `calc(${slideSpacingLg} * -1)`,
                  xl: `calc(${slideSpacingXl} * -1)`,
                },
              }}
            >
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => {
                  return (
                    <CardBox key={i} xlNum={xlNum} lgNum={lgNum} mdNum={mdNum}>
                      <ProductCardSkeleton />
                    </CardBox>
                  );
                })
              ) : (
                <>
                  {(!items || items.length <= 0) && <NotFoundCard />}
                  {items && items.length > 0 && (
                    <>
                      {items.map((slide, index) => {
                        return (
                          // embla__slide
                          <CardBox key={slide.code} xlNum={xlNum} lgNum={lgNum} mdNum={mdNum}>
                            <ProductCard product={slide} surface={surface} position={index} />
                          </CardBox>
                        );
                      })}
                      {showMore && (
                        <CardBox key={href} xlNum={xlNum} lgNum={lgNum} mdNum={mdNum}>
                          <CarouselShowMoreCard color={color} href={href as string} image={iconImage} svgIcon={icon} />
                        </CardBox>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Box>
          <CarouselFadeSides emblaApi={emblaApi as any} />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, py: 1.7 }}>
            <CarouselFooter emblaApi={emblaApi as any} />
          </Box>
        </Box>
      </Stack>
    </>
  );
}
